"use strict";

import Swiper, { Navigation, A11y, Pagination, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import "swiper/css/pagination";
import "swiper/css/a11y";

const SliderSingle = {
  els: null,

  init() {
    this.els = document.querySelectorAll(".f-slider.single--");
    this.els.forEach((el) => this.create(el));
  },

  create(el) {
    const slider = el.querySelector(".swiper");
    const slidesCount = slider.querySelectorAll(".swiper-slide").length;
    const modules = [A11y, Navigation, Pagination];
    const autoplayEnabled = slider.dataset.autoplay === "true";
    if (autoplayEnabled) modules.push(Autoplay);
    const nextBtn = slider.dataset.prev
      ? slider.dataset.prev
      : "Navigate to prev slide";
    const prevBtn = slider.dataset.next
      ? slider.dataset.next
      : "Navigate to next slide";

    // A single-slide swiper isn't a carousel: no landmark/name needed
    if (slidesCount > 1) {
      slider.setAttribute("role", "region");
      slider.setAttribute("aria-roledescription", "carrousel");
      if (slider.dataset.titleId) {
        slider.setAttribute("aria-labelledby", slider.dataset.titleId);
      } else if (slider.dataset.label) {
        slider.setAttribute("aria-label", slider.dataset.label);
      }
      // Single group for the whole reel, not one per slide
      slider.querySelector(".swiper-wrapper")?.setAttribute("role", "group");
    }

    const nextEl = el.querySelector(".f-slider__navigation-next");
    const prevEl = el.querySelector(".f-slider__navigation-prev");

    // Only focus the active slide's first link on a real user navigation
    // (arrows, pagination, swipe), never on internal transitions triggered on load/autoplay
    let userNavigated = false;

    const config = {
      modules,
      spaceBetween: 0,
      slidesPerView: 1,
      slidesPerGroup: 1,
      loop: autoplayEnabled,
      speed: 600,
      autoHeight: true,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: {
        enabled: slidesCount > 1,
        prevSlideMessage: nextBtn,
        nextSlideMessage: prevBtn,
        // Group role is on the wrapper (single group for the whole reel), not per slide
        slideRole: null,
      },
      on: {
        init() {
          const swiper = this;
          SliderSingle.updateSlideAccessibility(swiper, true);
          SliderSingle.updateNavAvailability(swiper, prevEl, nextEl);
          // Name comes from the visible (visually-hidden) span in the markup,
          // not from an aria-label — keeps it perceivable with CSS disabled.
          prevEl?.removeAttribute("aria-label");
          nextEl?.removeAttribute("aria-label");

          // Delegate on the pagination container (stable) rather than on
          // each bullet: Swiper re-renders the bullets when the grid changes
          // — which happens when the slider sits in a hidden tab that later
          // shows — and listeners bound to the original bullets would be lost.
          const paginationEl = swiper.pagination.el;
          if (paginationEl) {
            const goToBullet = (bullet) => {
              const index = parseInt(bullet.getAttribute("data-slide-index"), 10);
              userNavigated = true;
              setTimeout(() => {
                swiper.slideTo(index);
              }, 0);
            };

            paginationEl.addEventListener("click", (e) => {
              const bullet = e.target.closest("[data-slide-index]");
              if (bullet && paginationEl.contains(bullet)) goToBullet(bullet);
            });

            paginationEl.addEventListener("keydown", (e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              const bullet = e.target.closest("[data-slide-index]");
              if (bullet && paginationEl.contains(bullet)) {
                e.preventDefault();
                goToBullet(bullet);
              }
            });
          }
        },
        transitionEnd() {
          const swiper = this;
          SliderSingle.updateSlideAccessibility(swiper, !userNavigated);
          userNavigated = false;
          swiper.slides.forEach((slide, index) => {
            if (index !== swiper.activeIndex) {
              SliderSingle.pauseMediaInSlide(slide);
            }
          });
        },
        touchEnd() {
          const swiper = this;
          userNavigated = true;
          // Si le tap n'a déclenché aucune transition, on nettoie le flag pour ne
          // pas fausser une transition ultérieure sans rapport (ex: autoplay)
          requestAnimationFrame(() => {
            if (!swiper.animating) {
              userNavigated = false;
            }
          });
        },
        keyPress() {
          userNavigated = true;
        },
        "toEdge fromEdge lock unlock": function () {
          SliderSingle.updateNavAvailability(this, prevEl, nextEl);
        },
      },
    };

    // Autoplay (défilement automatique)
    if (autoplayEnabled) {
      config.autoplay = {
        delay: parseInt(slider.dataset.autoplayDelay, 10) || 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    // Navigation
    if (slider.dataset.navigation === "arrows") {
      config.navigation = { nextEl, prevEl };
      nextEl?.addEventListener("click", () => (userNavigated = true));
      prevEl?.addEventListener("click", () => (userNavigated = true));
    }

    // Pagination
    if (slider.dataset.pagination === "true") {
      const labelPrefix = slider.dataset.go || "Go to slide";
      config.pagination = {
        el: el.querySelector(".f-slider__pagination"),
        clickable: true,
        type: "bullets",
        renderBullet: (index, className) => {
          return `<li><span class="${className}" role="button" data-slide-index="${index}"  aria-label="${labelPrefix} ${index + 1}"><span class="visually-hidden">${index + 1}</span></span></li>`;
        },
      };
    }

    slider.swiper = new Swiper(slider, config);
  },
  // Swiper only sets `disabled` on the boundary nav button — some AT still
  // expose disabled native buttons in browse mode, so hide them outright.
  updateNavAvailability(swiper, prevEl, nextEl) {
    if (swiper.params.loop) return;
    if (prevEl) prevEl.hidden = swiper.isBeginning;
    if (nextEl) nextEl.hidden = swiper.isEnd;
  },
  updateSlideAccessibility(swiper, isTheInit = false) {
    //fonction accessibilité : enpecher focus sur slides masquées
    swiper.slides.forEach((slide, index) => {
      const isActive = index === swiper.activeIndex;

      slide.setAttribute("aria-hidden", String(!isActive));
      slide.removeAttribute("role");
      slide.removeAttribute("aria-label");

      const focusable = slide.querySelectorAll(
        'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])',
      );
      focusable.forEach((el) => {
        if (isActive) {
          el.removeAttribute("tabindex");
          el.removeAttribute("aria-hidden");
          el.removeAttribute("aria-disabled");
          // el.style.visibility = 'visible';
        } else {
          el.setAttribute("tabindex", "-1");
          el.setAttribute("aria-hidden", "true");
          el.setAttribute("aria-disabled", "true");
          // el.style.visibility = 'hidden';
        }
      });
    });

    // Donne le focus au premier lien/cta de la slide active ==> uniquement au slide et pas au init
    if (!isTheInit) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (activeSlide) {
        const firstLink = activeSlide.querySelector(
          "a, button, input, textarea, select, [tabindex]",
        );
        if (firstLink) {
          setTimeout(() => {
            firstLink.focus();
          }, 100);
        }
      }
    }
  },
  pauseMediaInSlide(slide) {
    slide.querySelector(".c-video__player-poster")?.classList.remove("hidden");
    // Si MP4
    const videos = slide.querySelectorAll("video");
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });

    // Si iframe (YouTube, Dailymotion,vimeo)
    const iframes = slide.querySelectorAll("iframe");
    iframes.forEach((oldIframe) => {
      let src = oldIframe.getAttribute("src") || "";

      // Enleve d'autoplay dans l'URL
      src = src
        .replace(/([?&])autoplay=1/, "$1autoplay=0")
        .replace(/([?&])autoPlay=1/, "$1autoPlay=0");
      src = src.replace(/[?&]+$/, "").replace(/\?&/, "?");

      // Crée un nouvel iframe
      const newIframe = oldIframe.cloneNode(true);
      newIframe.setAttribute("src", src);
      newIframe.setAttribute("allow", "fullscreen; encrypted-media");

      // Remplace l'ancien iframe
      oldIframe.replaceWith(newIframe);
    });
  },
};

export default SliderSingle;
